/**
 * JotForm embed with Cookiebot consent gate.
 *
 * The iframe is only injected after the visitor accepted the "marketing"
 * category. Until then a placeholder is shown that opens the consent dialog
 * on click.
 *
 * Element specific entry: prefixed with `_`, so it is not added to
 * Configuration/Generated/includes.typoscript. The content block loads it
 * itself via <f:asset.script> in
 * ContentBlocks/ContentElements/jotform/templates/frontend.html. Based on original JotForm snipped
 */

interface CookiebotConsent {
  marketing: boolean;
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
}

declare global {
  interface Window {
    Cookiebot?: { consent?: CookiebotConsent };
    CookieConsent?: { show?: () => void };
  }
}

// no imports in this entry - marks the file as a module so `declare global` works
export { };

const EMBED_SELECTOR = '.jotform-embed[data-jotform-id]';
const JOTFORM_ORIGINS = ['jotform.com', 'jotform.pro'];
const IFRAME_ID_PREFIX = 'JotFormIFrame-';

const NO_SCRIPT_MESSAGE =
  'Scripte zur Anzeige unserer Cookie-Bestätigung werden von Ihrem Browser geblockt. Bitte passen Sie Ihren Script-Blocker an, um unsere Formulare zu nutzen.';

/**
 * Checks whether a postMessage origin belongs to JotForm.
 */
const isPermitted = (originUrl: string, allowedDomains: string[]) => {
  const url = document.createElement('a');
  url.href = originUrl;
  const { hostname } = url;

  if (!hostname) {
    return false;
  }

  return allowedDomains.some((domain) => {
    return hostname === domain || hostname.endsWith(`.${domain}`);
  });
};

/**
 * Handles the postMessage protocol of the embedded JotForm iframe
 * (resizing, scrolling, fullscreen, ...).
 */
const handleIFrameMessage = (event: MessageEvent) => {
  if (typeof event.data !== 'string') {
    return;
  }

  const args = event.data.split(':');
  const iframe = document.getElementById(
    args.length > 2
      ? `${IFRAME_ID_PREFIX}${args[args.length - 1]}`
      : 'JotFormIFrame',
  ) as HTMLIFrameElement | null;

  if (!iframe) {
    return;
  }

  switch (args[0]) {
    case 'scrollIntoView':
      iframe.scrollIntoView();
      break;

    case 'setHeight': {
      const height = Number(args[1]);
      iframe.style.height = `${args[1]}px`;
      if (
        !Number.isNaN(height) &&
        parseInt(iframe.style.minHeight, 10) > height
      ) {
        iframe.style.minHeight = `${args[1]}px`;
      }
      break;
    }

    case 'collapseErrorPage':
      if (iframe.clientHeight > window.innerHeight) {
        iframe.style.height = `${window.innerHeight}px`;
      }
      break;

    case 'reloadPage':
      window.location.reload();
      break;

    case 'loadScript': {
      if (!isPermitted(event.origin, JOTFORM_ORIGINS)) {
        break;
      }
      // protocol separator was split away for absolute urls
      const src = args.length > 3 ? `${args[1]}:${args[2]}` : args[1];
      const script = document.createElement('script');
      script.src = src;
      script.type = 'text/javascript';
      document.body.appendChild(script);
      break;
    }

    case 'exitFullscreen':
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      break;

    default:
      break;
  }

  if (event.origin.indexOf('jotform') > -1 && iframe.contentWindow) {
    iframe.contentWindow.postMessage(
      JSON.stringify({
        type: 'urls',
        value: {
          docurl: encodeURIComponent(document.URL),
          referrer: encodeURIComponent(document.referrer),
        },
      }),
      '*',
    );
  }
};

/**
 * JotForm expects the query parameters of the embedding page to be forwarded
 * to the form, plus the `isIframeEmbed` marker.
 */
const buildIframeSrc = (jotformId: string) => {
  const url = new URL(`https://form.jotform.com/${jotformId}`);
  new URLSearchParams(window.location.search).forEach((value, key) => {
    url.searchParams.append(key, value);
  });
  url.searchParams.append('isIframeEmbed', '1');
  return url.toString();
};

/** embeds whose iframe was already injected */
const renderedEmbeds = new WeakSet<HTMLElement>();

/**
 * Replaces the consent placeholder with the actual form.
 */
const renderIframe = (embed: HTMLElement) => {
  const { jotformId, jotformTitle } = embed.dataset;

  if (!jotformId || renderedEmbeds.has(embed)) {
    return;
  }
  renderedEmbeds.add(embed);

  const iframe = document.createElement('iframe');
  iframe.id = `${IFRAME_ID_PREFIX}${jotformId}`;
  iframe.src = buildIframeSrc(jotformId);
  iframe.title = jotformTitle || 'Jotform Formular';
  iframe.allow = 'geolocation; microphone; camera';
  iframe.allowFullscreen = true;
  iframe.scrolling = 'no';
  iframe.setAttribute('data-cookieconsent', 'marketing');
  iframe.setAttribute('frameborder', '0');
  iframe.style.minWidth = '100%';
  iframe.style.maxWidth = '100%';
  iframe.style.border = 'none';
  iframe.addEventListener('load', () => {
    window.parent.scrollTo(0, 0);
  });

  embed.replaceChildren(iframe);
};

const openConsentDialog = () => {
  window.CookieConsent?.show?.();
};

/**
 * Initializes all JotForm embeds on the page.
 */
function init() {
  const embeds = Array.from(
    document.querySelectorAll<HTMLElement>(EMBED_SELECTOR),
  );

  if (embeds.length === 0) {
    return;
  }

  embeds.forEach((embed) => {
    embed.addEventListener('click', openConsentDialog);
  });

  window.addEventListener('message', handleIFrameMessage, false);

  const renderIfConsented = () => {
    if (!window.Cookiebot?.consent?.marketing) {
      return;
    }
    embeds.forEach((embed) => {
      embed.removeEventListener('click', openConsentDialog);
      renderIframe(embed);
    });
  };

  // fires on every page load once a consent decision exists
  window.addEventListener('CookiebotOnAccept', renderIfConsented);

  // in case the event already fired before this module was evaluated
  renderIfConsented();

  // Cookiebot is checked after load: with data-blockingmode="auto" the consent
  // state is not necessarily available at DOMContentLoaded yet.
  window.addEventListener('load', () => {
    renderIfConsented();

    // Cookiebot itself is blocked -> the placeholder can never be resolved
    if (window.Cookiebot?.consent) {
      return;
    }
    embeds.forEach((embed) => {
      const text = embed.querySelector('.consent-placeholder .text');
      if (text) {
        text.textContent = NO_SCRIPT_MESSAGE;
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
